import { inject, Injectable } from '@angular/core'
import { ClosePositionInterface } from '@modules/positions-module/interfaces/close-position.interface'
import { OrderTermEnum } from '@modules/orders-module/enums/order-term.enum'
import { OrderTypeEnum } from '@modules/orders-module/enums/order-type.enum'
import { PositionsInterface } from '@modules/positions-module/interfaces/positions.interface'
import { OrderInterface } from '@modules/orders-module/interfaces/order.interface'
import { PositionSideEnum } from '@modules/positions-module/enums/position-side.enum'
import { BehaviorSubject } from 'rxjs'
import { PositionsStocksParamsInterface } from '@modules/stocks-module/positions/interfaces/positions-stocks-params.interface'
import { AlpacaAccountHelperService } from '@modules/alpaca-module/alpaca-account/services/alpaca-account-helper.service'

@Injectable({
	providedIn: 'root',
})
export class PositionsStocksHelperService {
	protected readonly orderTypeEnum: typeof OrderTypeEnum = OrderTypeEnum
	protected readonly orderTermEnum: typeof OrderTermEnum = OrderTermEnum
	protected readonly positionsSideEnum: typeof PositionSideEnum = PositionSideEnum

	public positions$: BehaviorSubject<PositionsInterface[]> = new BehaviorSubject<PositionsInterface[]>([])
	public isLoadingPositions$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false)
	public positionsParams$: BehaviorSubject<PositionsStocksParamsInterface | null> =
		new BehaviorSubject<PositionsStocksParamsInterface | null>(null)

	private readonly _alpacaAccountHelperService: AlpacaAccountHelperService =
		inject<AlpacaAccountHelperService>(AlpacaAccountHelperService)

	public transformOrderForClosePosition(order: ClosePositionInterface): ClosePositionInterface {
		const isPositionTypeLimit: boolean = this.orderTypeEnum.LIMIT === order.type
		const isDayPlus: boolean = order.time_in_force === this.orderTermEnum.DAY_PLUS
		const timeInForce: OrderTermEnum = isDayPlus ? this.orderTermEnum.DAY : order.time_in_force

		const closedPosition: ClosePositionInterface = {
			...order,
			time_in_force: timeInForce,
			extended_hours: isDayPlus,
		}

		if (!isPositionTypeLimit) {
			delete closedPosition.limit_price
		}

		return closedPosition
	}

	public calcQtyAvailable(position: PositionsInterface | null, orders: OrderInterface[]): number {
		if (!position || !orders.length) return 0

		const sumQty: number = orders.reduce((acc: number, order: OrderInterface): number => acc + order.qty, 0)

		return position?.side === this.positionsSideEnum.SHORT ? +position.qty + sumQty : +(position?.qty ?? 0) - sumQty
	}

	public toggleLoaderPositions(value: boolean): void {
		const isHasPositions: boolean = !!this.positions$.getValue().length
		this.isLoadingPositions$.next(value && !isHasPositions)
	}

	public updateUnrealizedPL(): void {
		const positions: PositionsInterface[] = this.positions$.getValue()

		const dollar: number = positions.reduce((res: number, position: PositionsInterface): number => {
			return res + +(position?.unrealized_pl || 0)
		}, 0)

		const equity: number = +(this._alpacaAccountHelperService.alpacaAccount$.getValue()?.equity ?? 0)

		const totalProfit: number = positions.reduce((res: number, position: PositionsInterface): number => {
			return res + (+position.market_value - +position.cost_basis)
		}, 0)

		const percent: number = (totalProfit / equity) * 100 || 0

		this._alpacaAccountHelperService.updateAlpacaAccount({
			unrealizedPLDollar: dollar,
			unrealizedPLPercent: percent,
		})
	}
}
